import {
  Fragment,
  type Node as PMNode,
  type ResolvedPos,
  type Schema,
} from '@tiptap/pm/model';
import { type EditorState, TextSelection, type Transaction } from '@tiptap/pm/state';
import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';
import { buildDraftSectionContent } from './insert-draft-section';
import { splitContentAtLineBreak } from './split-content-at-line-break';
import { splitSectionAtParagraph } from './split-section';

/**
 * Matches a single `# ` at the very start of a textblock. The space the user
 * just typed is not in the document yet when the rule runs, so the match is
 * against the leading `#` plus the inbound space — `##` never matches.
 */
export const SECTION_HEADING_PATTERN = /^# $/;

/**
 * Pure handler for live `# ` → section conversion, shared by the vMarkdown and
 * markdown-native `createSectionInputRules` wrappers.
 *
 * Typing `# ` at the start of a top-level paragraph turns that paragraph into a
 * `sectionNode` that absorbs every following top-level block up to the next
 * section (or the end of the document) — a heading owns the content beneath it.
 * An empty line with nothing beneath becomes a draft section (auto-removed on
 * blur if left empty, promoted to committed once text is typed — same lifecycle
 * as the slash-command "New Section"). A line that already has text after `# `
 * keeps that text as the heading and commits immediately.
 *
 * Typing `# ` inside a section's body paragraph splits the section in two: the
 * trigger line and everything after it move into a new sibling section below
 * (see `splitSectionAtParagraph`). A real sibling section round-trips cleanly,
 * unlike a nested `<h1>` — which `stripSectionWrappers` would flatten back into
 * a top-level `# ` on reload. Lists, tables, headings and deeper nesting never
 * trigger conversion.
 */
export function applySectionInputRule({
  state,
  range,
}: {
  state: EditorState;
  range: { from: number; to: number };
}): null | void {
  const $start = state.doc.resolve(range.from);

  const { schema } = state;
  const { heading, paragraph, sectionNode } = schema.nodes;
  if (!heading || !paragraph || !sectionNode) return null;

  // Depth 1 + paragraph: a top-level line, not inside a section/table/list.
  const inTopLevelParagraph =
    $start.depth === 1 && $start.parent.type.name === 'paragraph';
  // Depth 2 + paragraph whose parent is a section: a section body line.
  const inSectionBodyParagraph =
    $start.depth === 2 &&
    $start.parent.type.name === 'paragraph' &&
    $start.node(1).type.name === 'sectionNode';

  if (!inTopLevelParagraph && !inSectionBodyParagraph) {
    return null;
  }

  const { tr } = state;

  // The typed space is never inserted, so deleting the matched range drops
  // the `#` and leaves the rest of the line as the heading text.
  tr.delete(range.from, range.to);

  convertParagraphToSection(tr, tr.doc.resolve(range.from), schema);
}

/**
 * Turns the paragraph at `$paragraph` (its leading `#` already removed) into a
 * section. A section-body paragraph splits its section in two; a top-level
 * paragraph becomes a new section that absorbs the following top-level blocks.
 * Shared by the anchored and non-leading input rules, and by controls that first
 * insert an empty trigger paragraph where a new untitled section should begin.
 */
export function convertParagraphToSection(
  tr: Transaction,
  $paragraph: ResolvedPos,
  schema: Schema
): void {
  const { heading, paragraph, sectionNode, hardBreak } = schema.nodes;
  if (!heading || !paragraph || !sectionNode) return;

  const inSectionBody =
    $paragraph.depth === 2 && $paragraph.node(1)?.type.name === 'sectionNode';
  if (inSectionBody) {
    splitSectionAtParagraph(tr, $paragraph, schema);
    return;
  }

  const paragraphStart = $paragraph.before($paragraph.depth);
  const paragraphEnd = $paragraph.after($paragraph.depth);
  const fullContent = $paragraph.parent.content;

  // Sections partition the document, so a new heading owns every following
  // top-level block up to the next section (or the end of the document).
  // Absorbing them here matches the reload-time grouping in
  // `wrapHtmlInSections` and the carry-down in `splitSectionAtParagraph`, so
  // the structure is right on the first keystroke, not only after a save.
  const docNode = $paragraph.node($paragraph.depth - 1);
  const triggerIndex = $paragraph.index($paragraph.depth - 1);
  const absorbedBlocks = [];
  let replaceEnd = paragraphEnd;
  for (let i = triggerIndex + 1; i < docNode.childCount; i++) {
    const sibling = docNode.child(i);
    if (sibling.type.name === 'sectionNode') break;
    absorbedBlocks.push(sibling);
    replaceEnd += sibling.nodeSize;
  }

  // A `**Header**\nbody` line with no blank line between is one paragraph
  // joined by a line break. Only the pre-break text is the heading; the rest
  // leads the body instead of being pulled into the title.
  const { headingContent, bodyContent } = splitContentAtLineBreak(fullContent, hardBreak);
  const hasHeadingText = headingContent.size > 0;
  const leadingBody =
    bodyContent && bodyContent.size > 0
      ? [paragraph.createChecked(null, bodyContent)]
      : [];
  const bodyBlocks = [...leadingBody, ...absorbedBlocks];

  let section;
  if (!hasHeadingText && bodyBlocks.length === 0) {
    // Empty line with nothing beneath: reuse the slash-command draft
    // (sentinel + placeholder title) so the blur-cleanup and promote-on-type
    // lifecycle apply.
    section = schema.nodeFromJSON(buildDraftSectionContent());
  } else if (!hasHeadingText) {
    // Empty heading that carries content down keeps the sentinel so it stays
    // parseable and the absorbed body is not orphaned on reload.
    const headingNode = heading.createChecked(
      { level: 1 },
      schema.text(DRAFT_SECTION_SENTINEL)
    );
    section = sectionNode.createChecked(
      { sectionTitle: 'New Section', isDraftSection: false },
      Fragment.from([headingNode, ...bodyBlocks])
    );
  } else {
    const headingNode = heading.createChecked({ level: 1 }, headingContent);
    // A section needs a body block; pad an empty paragraph when none remain.
    const body = bodyBlocks.length > 0 ? bodyBlocks : [paragraph.createChecked()];
    section = sectionNode.createChecked(
      { sectionTitle: headingNode.textContent || null },
      Fragment.from([headingNode, ...body])
    );
  }

  tr.replaceWith(paragraphStart, replaceEnd, section);

  // Land the cursor inside the heading: +1 enters the section, +1 the heading.
  tr.setSelection(TextSelection.create(tr.doc, paragraphStart + 2));
}

/**
 * Matches `# ` anywhere after the start of a paragraph. The leading match
 * character keeps the anchored `SECTION_HEADING_PATTERN` responsible for the
 * paragraph-start case and prevents `## ` from matching.
 *
 * Any preceding character qualifies, so prose like `Item # 5` converts too.
 * Backspace reverts it — Tiptap's core keymap binds that to `undoInputRule`.
 */
export const SECTION_NON_LEADING_HEADING_PATTERN = /[^#]# $/;

/**
 * Companion to `applySectionInputRule` for `# ` typed after existing paragraph
 * content. Splits the paragraph at the exact trigger position, then hands the
 * right-hand paragraph to `convertParagraphToSection`. A preceding soft/hard
 * line break remains a delimiter and is removed; ordinary inline content stays
 * in the original paragraph unchanged.
 */
export function applyNonLeadingSectionInputRule({
  state,
  range,
}: {
  state: EditorState;
  range: { from: number; to: number };
}): null | void {
  const { schema } = state;
  const { paragraph, hardBreak } = schema.nodes;
  if (!paragraph) return null;

  // The pattern includes one character before `#`; `range.to` sits immediately
  // after the already-inserted `#` (the inbound space is not in the document).
  const triggerPos = range.to - 1;
  const $trigger = state.doc.resolve(triggerPos);
  const inTopLevelParagraph =
    $trigger.depth === 1 && $trigger.parent.type.name === 'paragraph';
  const inSectionBodyParagraph =
    $trigger.depth === 2 &&
    $trigger.parent.type.name === 'paragraph' &&
    $trigger.node(1).type.name === 'sectionNode';
  if (!inTopLevelParagraph && !inSectionBodyParagraph) return null;

  const bodyParagraph = $trigger.parent;
  const triggerOffset = $trigger.parentOffset;
  if (triggerOffset <= 0) return null;

  const precedingOffset = triggerOffset - 1;
  const isHardBreak = $trigger.nodeBefore?.type === hardBreak;
  const isSoftBreak =
    !isHardBreak && bodyParagraph.textBetween(precedingOffset, triggerOffset) === '\n';

  const { tr } = state;
  const paragraphStart = $trigger.before($trigger.depth);

  // Split immediately before `#`. A visual line break introducing the trigger is
  // a delimiter, so it is dropped rather than kept on the left. Skip the `#`
  // itself on the right; the typed space is consumed by the input-rule runner.
  const preContentEnd = isHardBreak || isSoftBreak ? precedingOffset : triggerOffset;
  const preContent = bodyParagraph.content.cut(0, preContentEnd);
  const triggerContent = bodyParagraph.content.cut(triggerOffset + 1);
  const replacement: PMNode[] = [];
  if (preContent.size > 0) replacement.push(paragraph.createChecked(null, preContent));
  replacement.push(paragraph.createChecked(null, triggerContent));
  tr.replaceWith(
    paragraphStart,
    paragraphStart + bodyParagraph.nodeSize,
    Fragment.fromArray(replacement)
  );

  const triggerStart =
    preContent.size > 0 ? paragraphStart + replacement[0]!.nodeSize : paragraphStart;
  convertParagraphToSection(tr, tr.doc.resolve(triggerStart + 1), schema);
}
